import React from 'react';
import { useTranslation } from '../hooks/useTranslation';
import {
  PLAY_STORE_URL,
  APP_STORE_URL,
  STORE_QR,
  MOBILE_APPS_LIVE,
} from '../config/mobileApp';

const AndroidIcon = () => (
  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85a.637.637 0 00-.83.22l-1.88 3.24a11.43 11.43 0 00-8.94 0L5.65 5.67a.643.643 0 00-.87-.2.568.568 0 00-.22.83l1.84 3.18C2.92 11.03 1 14.22 1 17.75h22c0-3.53-1.92-6.72-5.4-8.27zM7 20h2v-2H7v2zm8 0h2v-2h-2v2zm-4 0h2v-2h-2v2z" />
  </svg>
);

const AppleIcon = () => (
  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.63 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
  </svg>
);

const StoreCard = ({
  variant,
  platform,
  title,
  description,
  storeUrl,
  qrUrl,
  storeLabel,
  comingSoonLabel,
}) => {
  const isAndroid = variant === 'android';
  const headerClass = isAndroid
    ? 'bg-gradient-to-r from-emerald-700 to-emerald-600'
    : 'bg-gradient-to-r from-slate-800 to-slate-700';

  const storeButtonClass = MOBILE_APPS_LIVE
    ? isAndroid
      ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-600/25'
      : 'bg-slate-900 hover:bg-black text-white shadow-lg shadow-slate-900/25'
    : 'bg-white/90 text-navy-900 border-2 border-white/80 hover:bg-white cursor-pointer';

  return (
    <article className="flex flex-col overflow-hidden rounded-2xl border border-navy-200/60 bg-white shadow-xl shadow-navy-900/5">
      {/* En-tête carte — badge intégré, sans chevauchement */}
      <div className={`flex items-center justify-between gap-3 px-5 py-4 ${headerClass}`}>
        <div className="flex items-center gap-3 min-w-0">
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-white/15 text-white">
            {isAndroid ? <AndroidIcon /> : <AppleIcon />}
          </span>
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wider text-white/80">{platform}</p>
            <h4 className="truncate text-base font-black text-white md:text-lg">{title}</h4>
          </div>
        </div>
        <span className="flex-shrink-0 rounded-lg bg-amber-400 px-3 py-1.5 text-[10px] font-black uppercase tracking-wider text-navy-900 shadow-sm">
          {comingSoonLabel}
        </span>
      </div>

      <div className="flex flex-col gap-6 p-5 sm:flex-row sm:items-center">
        {/* QR */}
        <div className="flex flex-col items-center sm:w-[148px] sm:flex-shrink-0">
          <div className="rounded-2xl border border-gray-200 bg-gray-50 p-3 shadow-inner">
            <img
              src={qrUrl}
              width={128}
              height={128}
              alt={`QR code ${platform} Golden Mali`}
              className="rounded-lg"
              loading="lazy"
            />
          </div>
          <p className="mt-2 text-center text-[11px] leading-snug text-gray-500">
            QR store
            <br />
            <span className="text-gray-400">(publication prochaine)</span>
          </p>
        </div>

        {/* Contenu */}
        <div className="flex min-w-0 flex-1 flex-col">
          <p className="mb-5 text-sm leading-relaxed text-gray-600">{description}</p>

          <a
            href={storeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex w-full items-center justify-center gap-2.5 rounded-xl px-5 py-3.5 text-sm font-bold transition-all duration-200 sm:w-auto ${storeButtonClass}`}
            aria-label={`${storeLabel} — ${comingSoonLabel}`}
            title={!MOBILE_APPS_LIVE ? comingSoonLabel : storeLabel}
          >
            {isAndroid ? <AndroidIcon /> : <AppleIcon />}
            <span>{storeLabel}</span>
            <svg className="h-4 w-4 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>

          {!MOBILE_APPS_LIVE && (
            <p className="mt-3 text-xs text-gray-400">
              Le lien store sera actif dès la publication officielle.
            </p>
          )}
        </div>
      </div>
    </article>
  );
};

const MobileStoresComingSoon = () => {
  const { t } = useTranslation();
  const comingSoon = t('landing.restricted.mobileStores.comingSoon');

  return (
    <section className="mb-8 overflow-hidden rounded-2xl border border-navy-200/50 bg-gradient-to-br from-white via-white to-navy-50/40 shadow-2xl shadow-navy-900/10">
      <div className="border-b border-navy-100 bg-navy-800 px-6 py-5 text-center md:px-8">
        <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-gold-500/20 ring-2 ring-gold-400/50">
          <svg className="h-6 w-6 text-gold-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        </div>
        <h3 className="text-xl font-black text-white md:text-2xl">
          {t('landing.restricted.mobileStores.title')}
        </h3>
        <p className="mx-auto mt-2 max-w-xl text-sm text-navy-100 md:text-base">
          {t('landing.restricted.mobileStores.subtitle')}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-5 p-5 md:grid-cols-2 md:gap-6 md:p-6">
        <StoreCard
          variant="android"
          platform="Android"
          title={t('landing.restricted.mobileStores.androidTitle')}
          description={t('landing.restricted.mobileStores.androidDescription')}
          storeUrl={PLAY_STORE_URL}
          qrUrl={STORE_QR.android}
          storeLabel={t('landing.restricted.mobileStores.playStoreButton')}
          comingSoonLabel={comingSoon}
        />
        <StoreCard
          variant="ios"
          platform="iOS"
          title={t('landing.restricted.mobileStores.iosTitle')}
          description={t('landing.restricted.mobileStores.iosDescription')}
          storeUrl={APP_STORE_URL}
          qrUrl={STORE_QR.ios}
          storeLabel={t('landing.restricted.mobileStores.appStoreButton')}
          comingSoonLabel={comingSoon}
        />
      </div>
    </section>
  );
};

export default MobileStoresComingSoon;
